import React, { useState } from 'react';
import styled from 'styled-components';
import { ChevronDown, ChevronUp, FileText, Hash, Tag } from 'lucide-react';
import { theme } from '../styles/theme';
import { Question } from '../types';

const CardContainer = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  transition: all ${theme.transitions.base};
  margin-bottom: ${theme.spacing[4]};

  &:hover {
    border-color: ${theme.colors.primary[400]};
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}20;
    transform: translateY(-1px);
  }
`;

const CardHeader = styled.div`
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  border-bottom: 1px solid ${theme.colors.border};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.primary[50]};
  }
`;

const QuestionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  flex: 1;
`;

const QuestionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${theme.colors.primary[100]};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.primary[600]};
`;

const QuestionContent = styled.div`
  flex: 1;
`;

const QuestionText = styled.h4`
  margin: 0 0 ${theme.spacing[2]} 0;
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.base};
  line-height: ${theme.typography.lineHeight.normal};
`;

const QuestionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
`;

const MetaBadge = styled.span<{ variant?: 'type' | 'section' | 'weight' | 'selection' }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};

  ${({ variant = 'type' }) => {
    switch (variant) {
      case 'type':
        return `
          background: ${theme.colors.primary[100]};
          color: ${theme.colors.primary[700]};
        `;
      case 'section':
        return `
          background: ${theme.colors.accent[100]};
          color: ${theme.colors.accent[700]};
        `;
      case 'weight':
        return `
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[700]};
        `;
      case 'selection':
        return `
          background: ${theme.colors.info[100]};
          color: ${theme.colors.info[700]};
          border: 1px solid ${theme.colors.info[500]};
          font-weight: ${theme.typography.fontWeight.semibold};
        `;
      default:
        return '';
    }
  }}
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.primary[100]};
    border-color: ${theme.colors.primary[400]};
    color: ${theme.colors.primary[600]};
  }
`;

const CardBody = styled.div<{ $expanded: boolean }>`
  max-height: ${({ $expanded }) => $expanded ? 'none' : '0'};
  opacity: ${({ $expanded }) => $expanded ? '1' : '0'};
  overflow: hidden;
  transition: all ${theme.transitions.base};
`;

const ResponseSection = styled.div`
  padding: ${theme.spacing[5]};
`;

const ResponseLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[3]};
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const ResponseContent = styled.div`
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
  color: ${theme.colors.textPrimary};
  white-space: pre-wrap;
  
  &:empty::before {
    content: 'No response provided yet...';
    color: ${theme.colors.textMuted};
    font-style: italic;
  }
`;

interface QuestionCardProps {
  question: Question;
  response?: string;
  index: number;
}

const QuestionCard: React.FC<QuestionCardProps> = ({ question, response, index }) => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <CardContainer>
      <CardHeader onClick={toggleExpanded}>
        <QuestionInfo>
          <QuestionIcon>
            <FileText size={20} />
          </QuestionIcon>
          <QuestionContent>
            <QuestionText>
              Question {index + 1}: {question.text}
            </QuestionText>
            <QuestionMeta>
              {question.type && (
                <MetaBadge variant="type">
                  <Tag size={12} />
                  {question.type}
                </MetaBadge>
              )}
              {question.section && (
                <MetaBadge variant="section">
                  <Hash size={12} />
                  {question.section}
                </MetaBadge>
              )}
              {question.weight && (
                <MetaBadge variant="weight">
                  Weight: {question.weight}
                </MetaBadge>
              )}
              {question.selection_score !== undefined && (
                <MetaBadge variant="selection" title="Question selection score">
                  Score: {Math.round(question.selection_score)}/100
                </MetaBadge>
              )}
            </QuestionMeta>
          </QuestionContent>
        </QuestionInfo>
        <ExpandButton>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </ExpandButton>
      </CardHeader>
      
      <CardBody $expanded={expanded}>
        <ResponseSection>
          <ResponseLabel>
            <FileText size={16} />
            Response
          </ResponseLabel>
          <ResponseContent>
            {question.response || response || ''}
          </ResponseContent>
        </ResponseSection>
      </CardBody>
    </CardContainer>
  );
};

export default QuestionCard;